import React from 'react';
import { AiOutlineTrophy } from 'react-icons/ai';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState } from '@/components/custom/empty-state';
import { cn } from '@/lib/utils';

const TROPHY_COLORS = ['#FFD661', '#8894AC', '#D3681A'];

export function RankingPosition({ index }) {
  if (index < 3) {
    return (
      <AiOutlineTrophy
        size={16}
        style={{ color: TROPHY_COLORS[index] }}
      />
    );
  }

  return <span className="text-xs text-neutral">{`#${String(index + 1).padStart(3, '0')}`}</span>;
}

const alignClass = align => (align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left');

export function RankingTable({ rows = [], columns = [], leadingLabel = '', leadingWidth = 220, renderLeading, emptyText = 'Nenhum dado disponível' }) {
  if (!rows.length) {
    return (
      <EmptyState
        type="data"
        variant="plain"
        text={emptyText}
      />
    );
  }

  // Colunas marcadas com `sticky: 'left' | 'right'` ficam fixas; as demais rolam horizontalmente.
  let leftOffset = leadingWidth;
  let rightOffset = 0;
  const stickyOffset = {};
  columns.forEach(column => {
    if (column.sticky === 'left') {
      stickyOffset[column.key] = leftOffset;
      leftOffset += column.width ?? 0;
    }
  });
  [...columns].reverse().forEach(column => {
    if (column.sticky === 'right') {
      stickyOffset[column.key] = rightOffset;
      rightOffset += column.width ?? 0;
    }
  });
  const stickyClass = column => column.sticky && 'sticky z-10 bg-white shadow-none';
  const stickyStyle = column => (column.sticky ? { [column.sticky]: stickyOffset[column.key] } : undefined);
  const tooltipId = React.useId();

  return (
    <>
      <ReactTooltip
        id={tooltipId}
        className="tooltip"
      />
      <Table className="min-w-[720px]">
        <TableHeader>
          <TableRow>
            <TableHead
              className="sticky left-0 z-10 bg-white shadow-none"
              style={{ width: leadingWidth }}
            >
              {leadingLabel}
            </TableHead>
            {columns.map(column => (
              <TableHead
                key={column.key}
                className={cn(alignClass(column.align), stickyClass(column))}
                style={{ width: column.width, ...stickyStyle(column) }}
              >
                <span
                  className="block truncate"
                  data-tooltip-id={typeof column.label === 'string' ? tooltipId : undefined}
                  data-tooltip-content={typeof column.label === 'string' ? column.label : undefined}
                >
                  {column.label}
                </span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>

        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.id ?? index}>
              <TableCell className="sticky left-0 z-10 bg-white shadow-none">{renderLeading(row, index)}</TableCell>
              {columns.map(column => (
                <TableCell
                  key={column.key}
                  className={cn(alignClass(column.align), stickyClass(column))}
                  style={stickyStyle(column)}
                >
                  {column.render ? column.render(row, index) : (row[column.key] ?? '-')}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}
